import { useMemo } from "react";
import { useTimeTrackerStore } from "../store/timeTrackerStore";
import {
  getCurrentDayRange,
  getCurrentWeekRange,
} from "../utils/analyticsPeriods";
import { formatDays, hoursToDays } from "../utils/billableDays";
import { useUrlParam } from "../utils/useUrlState";
import { modalCodec } from "../utils/modalParam";
import { Card, Kbd, MetricRow } from "@lazar/ui";

const hoursFromMs = (ms: number) => Number((ms / (1000 * 60 * 60)).toFixed(1));

const WeeklyClientSummary = () => {
  const [, setModal] = useUrlParam("modal", modalCodec);
  const { getClient, timeEntries, settings } = useTimeTrackerStore();
  const dayHours = settings.dayHours;

  const { totalHours, todayHours, perClient } = useMemo(() => {
    const weekRange = getCurrentWeekRange();
    const weekStartMs = weekRange.start.getTime();
    const weekEndMs = weekRange.end.getTime();

    const dayRange = getCurrentDayRange();
    const dayStartMs = dayRange.start.getTime();
    const dayEndMs = dayRange.end.getTime();

    const entries = Object.values(timeEntries).filter(
      (entry) =>
        !!entry.endTime &&
        entry.startTime >= weekStartMs &&
        entry.startTime <= weekEndMs,
    );

    let total = 0;
    let today = 0;
    const byClient: Record<string, number> = {};

    entries.forEach((entry) => {
      if (!entry.endTime) return;
      const hours = hoursFromMs(entry.endTime - entry.startTime);
      total += hours;
      if (entry.startTime >= dayStartMs && entry.startTime <= dayEndMs) {
        today += hours;
      }
      const id = entry.clientId || "unassigned";
      byClient[id] = (byClient[id] || 0) + hours;
    });

    const rows = Object.entries(byClient)
      .map(([id, hours]) => {
        const client = getClient(id);
        const baseName = client?.name || "No client";
        return {
          id,
          name:
            client?.archivedAt !== undefined
              ? `${baseName} (archived)`
              : baseName,
          color: client?.color || "#CCCCCC",
          hours,
          targetDays: client?.weeklyTargetDays,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

    return { totalHours: total, todayHours: today, perClient: rows };
  }, [timeEntries, getClient]);

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-black dark:text-cream">This Week</h2>
        <button
          onClick={() => setModal("analytics")}
          className="cursor-pointer text-sm font-bold text-ink dark:text-cream hover:underline underline-offset-4 inline-flex items-center gap-1.5"
        >
          Detailed analytics
          <Kbd>A</Kbd>
        </button>
      </div>

      <p className="text-ink dark:text-cream font-bold text-2xl">
        {formatDays(hoursToDays(totalHours, dayHours))}{" "}
        <span className="text-ink-soft dark:text-cream/60 text-sm font-medium">
          (≈ {totalHours.toFixed(1)}h)
        </span>
      </p>
      <p className="text-ink-soft dark:text-cream/70 text-sm font-bold mb-4">
        Today: {formatDays(hoursToDays(todayHours, dayHours))}{" "}
        <span className="text-ink-soft dark:text-cream/60 font-medium">
          (≈ {todayHours.toFixed(1)}h)
        </span>
      </p>

      {perClient.length === 0 ? (
        <p className="text-ink-soft dark:text-cream/70 text-sm">
          No time tracked yet this week.
        </p>
      ) : (
        <div className="space-y-2">
          {perClient.map((client) => {
            const days = hoursToDays(client.hours, dayHours);
            const targetDays = client.targetDays;
            return (
              <Card sub key={client.id} className="text-sm p-3">
                <MetricRow
                  leadingClassName="pr-2"
                  leading={
                    <>
                      <div
                        className="w-3 h-3 rounded-full mr-2 flex-shrink-0 border-2 border-ink dark:border-cream"
                        style={{ backgroundColor: client.color }}
                      />
                      <span className="text-ink dark:text-cream font-bold truncate">
                        {client.name}
                      </span>
                    </>
                  }
                  valueClassName="text-ink dark:text-cream/80 ml-2 font-mono text-xs text-right"
                  value={
                    targetDays ? (
                      <>
                        <div className="font-bold">
                          {formatDays(days)} / {formatDays(targetDays)}
                        </div>
                        <div className="text-ink-soft dark:text-cream/50">
                          ({client.hours.toFixed(1)}h /{" "}
                          {(targetDays * dayHours).toFixed(1)}h)
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="font-bold">{formatDays(days)}</div>
                        <div className="text-ink-soft dark:text-cream/50">
                          ({client.hours.toFixed(1)}h)
                        </div>
                      </>
                    )
                  }
                />
              </Card>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default WeeklyClientSummary;
